/**
 * WER SCHREIBT, MELDET ES. WER ANZEIGT, ZIEHT NACH.
 * ============================================================================
 *
 *   ORIGINALTEXT MARVIN (09.09.2026)
 *     „Wir haben immernoch das Problem das die Eingaben nixht gepusht werden.
 *      Ich muss immer den aktuellen Bereich neu laden um die neuen Daten zu
 *      sehen, zum Beispiel auch wenn ein Auftrag vorbereitet wird. Wenn ich
 *      alles eingetragen und gespeichert habe, das popup schließe, dann
 *      wechselt der Text des Chips nicht und wird nicht grün. Das geht nicht.
 *      Überall müssen alle Änderungen live gepusht werden."
 *
 * SEIN BEISPIEL IST DER GANZE BEFUND, UND ER WAR ABZUZÄHLEN. Ein Fenster, das
 * etwas speichert, sagte dem Bildschirm dahinter, dass es zugeht - und nicht,
 * dass sich etwas geändert hat. Von den Aufrufstellen in der Auftragsakte
 * trugen einige ein `fertig={laden}` und die übrigen nur ein
 * `zu={() => setDialog(null)}`; welche von beidem dastand, entschied, ob der
 * Bildschirm hinterher stimmte.
 *
 * > **Eine Regel, die an dreissig Aufrufstellen stehen muss, wird bei der
 * > einunddreissigsten vergessen** - und dieses Haus hat den Satz an der
 * > Sichtbarkeitsgrenze (24.08.2026), an den Dateinamen (01.09.2026) und am
 * > Verfasser einer Auftragsinfo (07.09.2026) schon dreimal bezahlt.
 *
 * DESHALB STEHT ES HIER UND AN EINER STELLE. Jeder SCHREIBENDE Aufruf meldet
 * sich beim Durchgang durch `api.ts`; jede Stelle, die Daten HOLT, meldet ihre
 * Ladefunktion hier an und wird gerufen. Kein Fenster muss mehr wissen, wer
 * hinter ihm steht.
 *
 * ----------------------------------------------------------------------------
 * WAS SIE NICHT IST
 * ----------------------------------------------------------------------------
 *
 * SIE IST KEIN ZWEITER WEG ZU DEN DATEN. Sie holt nichts, sie kennt keine
 * Adresse und keine Antwort - sie ruft die Ladefunktion, die der Bildschirm
 * ohnehin hat. Damit gilt jede Sichtbarkeitsgrenze und jedes Recht
 * unverändert: **was jemand sieht, entscheidet weiter der Server.**
 *
 * UND SIE IST KEIN RIEGEL. Ein Nachzug, der scheitert, hält nichts auf; was
 * dabei schiefgeht, gehört in die Anzeige des Bildschirms und nicht hierher.
 *
 * NUR SCHREIBENDE AUFRUFE MELDEN. Meldete ein GET, riefe jeder Nachzug den
 * nächsten - eine Schleife, die den Server so lange belastet, bis jemand die
 * Seite schliesst. Dieses Haus hat genau das am 02.09.2026 beim Nachladen der
 * Auftragsliste bezahlt, und dort war es nicht einmal zu sehen.
 */

/** Eine Ladefunktion. Was sie zurückgibt, geht diese Datei nichts an. */
export type Nachzug = () => unknown;

/** Was ein Melder mitgibt: der Weg, der geschrieben hat, und die Art. */
export interface Aenderung {
  /** Der Pfad ohne `/api`, so wie ihn `api.ts` ruft. */
  weg: string;
  /** GET, POST, PUT, PATCH, DELETE. */
  art: string;
}

const horcher = new Set<Nachzug>();

/**
 * Eine Ladefunktion anmelden. Zurück kommt der Weg, sie wieder abzumelden.
 *
 * **DAS ABMELDEN GEHÖRT ZUM ANMELDEN**, und zwar hier und nicht beim
 * Aufrufer: ein Bildschirm, der weg ist und weiter nachzieht, holt Daten für
 * ein Fenster, das niemand mehr ansieht - und schreibt seine Antwort in einen
 * Zustand, den React längst abgeräumt hat.
 */
export function beiAenderung(nachzug: Nachzug): () => void {
  horcher.add(nachzug);
  return () => { horcher.delete(nachzug); };
}

/**
 * Etwas wurde geschrieben.
 *
 * EIN NACHZUG, DER WIRFT, HÄLT DIE ÜBRIGEN NICHT AUF. Sonst entscheidet die
 * Reihenfolge, welcher Bildschirm hinterher stimmt - und die Reihenfolge einer
 * Menge ist die des Anmeldens und damit zufällig.
 */
export function meldeAenderung(a: Aenderung): void {
  if (!schreibt(a.art)) return;
  for (const h of [...horcher]) {
    try { h(); } catch { /* ein Nachzug ist eine Auskunft und kein Riegel */ }
  }
}

/**
 * Schreibt dieser Aufruf?
 *
 * GEMESSEN WIRD DIE ART UND NICHT DER WEG. Ein `POST /auftraege/suche` wäre
 * nach dem Weg eine Suche und nach der Art ein Schreiben; welche der beiden
 * Auskünfte stimmt, weiss nur der Server. Hier gilt die vorsichtige: ein
 * Nachzug zu viel kostet eine Abfrage, ein Nachzug zu wenig kostet genau das,
 * was Marvin beanstandet hat.
 */
export function schreibt(art: string): boolean {
  const a = (art || 'GET').trim().toUpperCase();
  return a !== 'GET' && a !== 'HEAD' && a !== 'OPTIONS';
}

/** Wie viele Stellen gerade nachziehen. Für den Prüfstand, nicht für die Anzeige. */
export function angemeldet(): number {
  return horcher.size;
}

/**
 * ETWAS WURDE WOANDERS GESCHRIEBEN (23.09.2026).
 *
 * Von einem anderen Menschen, von der Automatik, von einem Import. Der Puls
 * (`teile/puls.ts`) ruft das, wenn der Server eine Änderung meldet, die
 * nicht aus diesem Fenster kam. Jede angemeldete Ladefunktion zieht nach -
 * dieselben wie nach einem eigenen Schreiben.
 */
export function allesNachziehen(): void {
  for (const h of [...horcher]) {
    try { h(); } catch { /* ein Nachzug ist eine Auskunft und kein Riegel */ }
  }
}
